/// <reference path="../../css.d.ts" />
"use client";

// Source : UIArc `registry/components/dropdown-menu` (MIT © 2026 Elia Kuratli), commit 7238f98196b147a8d50ce467abcbad310d4d8414.
// Adaptations UBM (lot U2) :
//   · ouverture LOGIQUE sous l'engagement d'hôte : Radix reçoit `open && engagé` ; une fenêtre qui passe derrière retire
//     son menu (portail, pile Radix, verrous modaux) sans `onOpenChange` et le rend au réengagement ;
//   · Content : marque de couche non modale de la pile commune (`@ubm/ui/overlays`) + pont d'Échap ;
//   · parts composables compatibles Radix (Root/Trigger/Portal/Content/Item/Separator/Label/Group/…) pour les
//     compositions (U3 PageHeader), sans fuite d'import Radix hors `@ubm/ui` ;
//   · `useMenuHighlight` : le surlignage glissant de la source, réemployable par la façade `@ubm/product-ui` Menu.
// Rendu, styles et mouvements (largeur morphée, libellé échangé, surlignage glissant) : ceux de la source.
import { Fragment, createContext, forwardRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ComponentPropsWithoutRef, CSSProperties, ElementRef, FocusEvent, ReactNode } from "react";
import * as DropdownPrimitive from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion, useReducedMotionConfig as useReducedMotion } from "motion/react";
import { ChevronDown } from "lucide-react";
import { motionTokens } from "../../lib/motion-tokens";
import { useOverlayHostEngaged, cederEchap } from "../../overlays/OverlayHost";
import { estOverlayDuDessus, marqueOverlayNonModal } from "../../overlays/useOverlayAccessible";
import "../../styles/foundation.css";
import styles from "./dropdown-menu.module.css";

export interface DropdownItem { label: string; onSelect?: () => void; disabled?: boolean; icon?: ReactNode; destructive?: boolean; separatorBefore?: boolean; }
export interface DropdownMenuProps { label: string; items: DropdownItem[]; icon?: ReactNode; }

type Highlight = { top: number; height: number; danger: boolean; glide: boolean };

/**
 * Classes de la peau source, pour les façades qui rendent le même menu sur un autre moteur. `surface` (lot E) : la surface
 * STATIQUE de `menu` (mêmes règles, sans transform ni mouvement) pour un panneau dont les sous-menus sont `fixed` à
 * l'intérieur ; l'élément actif d'une navigation contrôlée se peint par `data-active` sur `item`.
 */
export const dropdownMenuClassNames = {
  menu: styles.menu as string,
  surface: styles.surface as string,
  item: styles.item as string,
  icon: styles.icon as string,
  separator: styles.separator as string,
  destructive: styles.destructive as string,
  highlight: styles.highlight as string,
  note: styles.note as string,
};

const MenuEngagement = createContext(true);

export interface DropdownMenuRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Modal (Radix) : défaut `true`. */
  modal?: boolean;
  dir?: "ltr" | "rtl";
  children?: ReactNode;
}

/** Root compatible Radix, à ouverture logique : sous un hôte désengagé, Radix reçoit `false` sans aucune notification. */
export function DropdownMenuRoot({ open: openProp, defaultOpen = false, onOpenChange, modal, dir, children }: DropdownMenuRootProps) {
  const engaged = useOverlayHostEngaged();
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const open = openProp ?? uncontrolled;
  const setOpen = useCallback((next: boolean) => {
    if (openProp === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  }, [openProp, onOpenChange]);
  return <MenuEngagement.Provider value={engaged}>
    <DropdownPrimitive.Root open={open && engaged} onOpenChange={setOpen} modal={modal} dir={dir}>{children}</DropdownPrimitive.Root>
  </MenuEngagement.Provider>;
}

/** Contenu bas niveau (sans portail ni peau) : marque de couche commune + pont d'Échap. */
export const DropdownMenuContentBase = forwardRef<
  ElementRef<typeof DropdownPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DropdownPrimitive.Content>
>(function DropdownMenuContentBase({ onEscapeKeyDown, ...props }, ref) {
  const engaged = useContext(MenuEngagement);
  const reduced = useReducedMotion();
  const node = useRef<HTMLDivElement | null>(null);
  const composed = useMemo(() => (el: HTMLDivElement | null) => {
    node.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  }, [ref]);
  return <DropdownPrimitive.Content {...marqueOverlayNonModal} data-motion={reduced ? "reduced" : undefined} {...props} ref={composed} onEscapeKeyDown={event => {
    onEscapeKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (!engaged || (node.current !== null && !estOverlayDuDessus(node.current))) cederEchap(event);
  }}/>;
});

DropdownMenuContentBase.displayName = "DropdownMenuContentBase";

export const DropdownMenuTrigger = DropdownPrimitive.Trigger;
export const DropdownMenuPortal = DropdownPrimitive.Portal;
export const DropdownMenuItem = DropdownPrimitive.Item;
export const DropdownMenuSeparator = DropdownPrimitive.Separator;
export const DropdownMenuLabel = DropdownPrimitive.Label;
export const DropdownMenuGroup = DropdownPrimitive.Group;
export const DropdownMenuCheckboxItem = DropdownPrimitive.CheckboxItem;
export const DropdownMenuRadioGroup = DropdownPrimitive.RadioGroup;
export const DropdownMenuRadioItem = DropdownPrimitive.RadioItem;
export const DropdownMenuItemIndicator = DropdownPrimitive.ItemIndicator;
export const DropdownMenuSub = DropdownPrimitive.Sub;
export const DropdownMenuSubTrigger = DropdownPrimitive.SubTrigger;
export const DropdownMenuSubContent = DropdownPrimitive.SubContent;
export const DropdownMenuArrow = DropdownPrimitive.Arrow;

/**
 * The source highlight: one highlight glides between items for the pointer and jumps instantly for the keyboard. Returns the
 * handlers for the menu panel and the highlight element; `reset()` clears it when the menu opens.
 */
export function useMenuHighlight() {
  const reduced = useReducedMotion();
  const [highlight, setHighlight] = useState<Highlight | null>(null);
  const pointer = useRef(false);
  const clearTimer = useRef(0);
  useEffect(() => () => window.clearTimeout(clearTimer.current), []);
  // Radix focuses the highlighted item (pointer or keyboard) and the content when the pointer leaves an item.
  function onMenuFocus(event: FocusEvent<HTMLElement>) {
    const item = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>('[role="menuitem"]') : null;
    window.clearTimeout(clearTimer.current);
    if (!item) {
      // A short grace period keeps the highlight gliding across separators and item gaps.
      clearTimer.current = window.setTimeout(() => setHighlight(null), pointer.current ? 70 : 0);
      return;
    }
    const next = { top: item.offsetTop, height: item.offsetHeight, danger: item.dataset.tone === "danger" };
    const glide = pointer.current;
    setHighlight(current => ({ ...next, glide: glide && current !== null }));
  }
  const reset = useCallback(() => { window.clearTimeout(clearTimer.current); setHighlight(null); }, []);
  const panelProps = {
    onFocus: onMenuFocus,
    onPointerMoveCapture: () => { pointer.current = true; },
    onKeyDownCapture: () => { pointer.current = false; },
  };
  const element = <motion.span className={styles.highlight} data-tone={highlight?.danger ? "danger" : undefined} aria-hidden="true" initial={false} animate={highlight ? { y: highlight.top, height: highlight.height, opacity: 1 } : { opacity: 0 }} transition={{ default: highlight?.glide && !reduced ? motionTokens.spring.snappy : { duration: 0 }, opacity: { duration: reduced ? 0 : .08 } }}/>;
  return { reset, panelProps, element };
}

/** A new trigger label rises in while the old one leaves, and the trigger width springs to the measured text instead of snapping. */
export function DropdownTriggerLabel({ text }: { text: string }) {
  const reduced = useReducedMotion();
  const measure = useRef<HTMLSpanElement>(null);
  const measured = useRef<string | null>(null);
  const [size, setSize] = useState<{ width: number | "auto"; animate: boolean }>({ width: "auto", animate: false });
  useLayoutEffect(() => {
    const node = measure.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      const current = node.textContent;
      // Only a text change morphs; the first measure and font swaps settle instantly.
      const animate = measured.current !== null && measured.current !== current;
      measured.current = current;
      setSize({ width: Math.ceil(entry?.borderBoxSize?.[0]?.inlineSize ?? node.offsetWidth), animate });
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <motion.span className={styles.label} initial={false} animate={{ width: size.width }} transition={size.animate && !reduced ? motionTokens.spring.morph : { duration: 0 }}>
    <span ref={measure} className={styles.labelMeasure} aria-hidden="true">{text}</span>
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span key={text} className={styles.labelText} initial={reduced ? false : { opacity: 0, y: "0.3em", filter: `blur(${motionTokens.blur.soft}px)` }} animate={{ opacity: 1, y: 0, filter: "blur(0px)" }} exit={reduced ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, y: "-0.3em", filter: `blur(${motionTokens.blur.subtle}px)`, transition: { duration: motionTokens.duration.fast, ease: [...motionTokens.ease.standard] } }} transition={{ duration: motionTokens.duration.standard, ease: [...motionTokens.ease.enter] }}>{text}</motion.span>
    </AnimatePresence>
  </motion.span>;
}

export function DropdownMenu({ label, items, icon }: DropdownMenuProps) {
  const highlight = useMenuHighlight();
  return <DropdownMenuRoot onOpenChange={open => { if (open) highlight.reset(); }}>
    <DropdownPrimitive.Trigger className={styles.trigger} type="button">{icon && <span className={styles.triggerIcon} aria-hidden="true">{icon}</span>}<DropdownTriggerLabel text={label}/><ChevronDown className={styles.chevron} size={15} strokeWidth={1.8} aria-hidden="true"/></DropdownPrimitive.Trigger>
    <DropdownPrimitive.Portal><DropdownMenuContentBase className={styles.menu} sideOffset={6} align="end" collisionPadding={12} loop {...highlight.panelProps}>
      {/* One highlight glides between items for the pointer and jumps instantly for the keyboard. */}
      {highlight.element}
      {items.map((item, index) => <Fragment key={item.label}>{item.separatorBefore && <DropdownPrimitive.Separator className={styles.separator}/>}<DropdownPrimitive.Item className={[styles.item, item.destructive ? styles.destructive : ""].filter(Boolean).join(" ")} data-tone={item.destructive ? "danger" : undefined} style={{ "--i": index } as CSSProperties} disabled={item.disabled} onSelect={item.onSelect}>{item.icon && <span className={styles.icon} aria-hidden="true">{item.icon}</span>}{item.label}</DropdownPrimitive.Item></Fragment>)}
    </DropdownMenuContentBase></DropdownPrimitive.Portal>
  </DropdownMenuRoot>;
}

export default DropdownMenu;
